import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { http } from "../../lib/http";
import { todayToronto, nextServiceDayToronto, tomorrowToronto, minStartDateToronto } from "../../lib/time";
import { lookupPlanPrice, lookupTruckersPrice, sumAddOnsPrice, fetchLivePricing } from "../../lib/pricing";
import PricingCalculator from "../../components/public/PricingCalculator";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { CheckCircle, ShieldCheck, Mail, CreditCard, ChevronRight, ChevronLeft, CalendarDays, MapPin, User, Utensils, Truck } from "lucide-react";

// Animation Variants
const slideVariants = {
    enter: (direction) => ({
        x: direction > 0 ? 50 : -50,
        opacity: 0
    }),
    center: {
        zIndex: 1,
        x: 0,
        opacity: 1
    },
    exit: (direction) => ({
        zIndex: 0,
        x: direction < 0 ? 50 : -50,
        opacity: 0
    })
};

// Reusable animated option card
const OptionCard = ({ selected, onClick, icon: Icon, title, description, badge }) => (
    <motion.div
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={onClick}
        className={`cursor-pointer border-2 rounded-2xl p-6 transition-all duration-300 flex flex-col items-center justify-center text-center gap-3 relative overflow-hidden
            ${selected
                ? "bg-orange-500/10 border-orange-500 shadow-lg shadow-orange-500/20"
                : "bg-gray-900 border-gray-700 hover:border-gray-500"}`}
    >
        {selected && (
            <motion.div
                layoutId="outline"
                className="absolute inset-0 border-2 border-orange-500 rounded-2xl"
                initial={false}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
            />
        )}
        {badge && (
            <span className="absolute top-3 right-3 bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                {badge}
            </span>
        )}
        <div className={`p-4 rounded-full ${selected ? "bg-orange-500 text-white" : "bg-gray-800 text-gray-400"}`}>
            {Icon && <Icon size={32} />}
        </div>
        <div>
            <h3 className={`font-bold text-lg ${selected ? "text-white" : "text-gray-200"}`}>{title}</h3>
            {description && <p className="text-sm text-gray-400 mt-1">{description}</p>}
        </div>
        {selected && (
            <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute bottom-3 right-3 text-orange-500"
            >
                <CheckCircle size={20} />
            </motion.div>
        )}
    </motion.div>
);

export default function SignupWizard() {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [direction, setDirection] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // Auth State
    const [tempToken, setTempToken] = useState(null);
    const [email, setEmail] = useState("");
    const [otp, setOtp] = useState("");

    // Unified Form State
    const [values, setValues] = useState({
        planCategory: "", // "Standard" or "Trucker"
        iam: "",
        firstName: "",
        lastName: "",
        contact: "",
        street: "",
        city: "",
        postal: "",
        itsA: "",
        buzz: "",
        planType: "",
        planTerm: "",
        nvPref: "",
        nvDays: [],
        addOns: [],
        billingTerm: "Weekly",
        startDate: nextServiceDayToronto(),
        allergies: "",
        password: ""
    });

    const [calculatedAmount, setCalculatedAmount] = useState(0);

    // Load live pricing
    useEffect(() => {
        fetchLivePricing().catch(console.error);
    }, []);

    // Configuration
    const TOTAL_STEPS = 9;

    const standardPlanTerms = {
        "2 Meals a Day": ["5 Days Veg", "5 Days Non-Veg", "2 Days Non-Veg, 3 Days Veg"],
        "3 Meals a Day": ["5 Days Veg", "5 Days Non-Veg", "2 Days Non-Veg, 3 Days Veg"]
    };

    const truckerPlanTerms = {
        "Truckers 10 Meals a Week": ["5 Veg + 5 Non-Veg", "10 Veg Meals", "10 Non-Veg Meals"],
        "Truckers 12 Meals a Week": ["6 Veg + 6 Non-Veg", "12 Veg Meals"]
    };

    const nvPrefOptions = {
        "2 Meals a Day": {
            "5 Days Non-Veg": ["1 NV + 1 Veg", "Both NV"],
            "2 Days Non-Veg, 3 Days Veg": ["1 NV + 1 Veg", "Both NV"]
        },
        "3 Meals a Day": {
            "5 Days Non-Veg": ["1 NV/day", "2 NV/day"],
            "2 Days Non-Veg, 3 Days Veg": ["1 NV/day", "2 NV/day"]
        }
    };

    // --- Navigation Handlers ---
    const nextStep = () => {
        if (!validateCurrentStep()) return;

        // Skip OTP step if user is standard Plan but we want to do checkout directly 
        // OR handle email Auth normally. Let's keep the flow: 
        // 1. Welcome, 2. Category, 3. Plan, 4. Prefs, 5. Addons, 6. Email/OTP, 7. Details, 8. Review

        setError("");
        if (step === 7 && !tempToken) {
            handleEmailSubmit(); // Will handle OTP internally
            return;
        }

        setDirection(1);
        setStep(prev => Math.min(prev + 1, TOTAL_STEPS));
    };

    const prevStep = () => {
        setError("");
        setDirection(-1);
        // If we are at step 7 and have sent email but not verified OTP, clicking back should just change step to 6
        setStep(prev => Math.max(prev - 1, 1));
    };

    const handleChange = (field, value) => {
        setValues(prev => ({ ...prev, [field]: value }));
    };

    const handleAddOnToggle = (addon) => {
        setValues(prev => ({
            ...prev,
            addOns: prev.addOns.includes(addon)
                ? prev.addOns.filter(a => a !== addon)
                : [...prev.addOns, addon]
        }));
    };

    // --- Validation ---
    const validateCurrentStep = () => {
        setError("");
        switch (step) {
            case 1:
                if (!values.iam) { setError("Please tell us who you are"); return false; }
                break;
            case 2:
                if (!values.planCategory) { setError("Please choose a plan category"); return false; }
                break;
            case 3:
                if (!values.planType || !values.planTerm) { setError("Please select your meal plan details"); return false; }
                break;
            case 4:
                // Preferences
                if (values.planCategory === "Standard") {
                    const needsNVPref = values.planTerm && values.planTerm.includes("Non-Veg") && values.planTerm !== "5 Days Veg";
                    if (needsNVPref && !values.nvPref) { setError("Please select your Non-Veg preference"); return false; }
                    const needs2NVDays = values.planTerm === "2 Days Non-Veg, 3 Days Veg";
                    if (needs2NVDays && (!values.nvDays || values.nvDays.length !== 2)) { setError("Please select exactly 2 Non-Veg days"); return false; }
                }
                break;
            case 5:
                // Addons (Optional)
                break;
            case 6:
                // Just viewing pricing, no specific state validation needed
                calculateCartTotal(); // ensure prices are ready for checkout soon
                break;
            case 7:
                // Email is validated in handleEmailSubmit/handleOtpSubmit
                if (!tempToken) return false;
                break;
            case 8:
                if (!values.firstName || !values.lastName || !values.contact) { setError("Please complete your personal information"); return false; }
                if (!values.street || !values.city || !values.postal || !values.itsA) { setError("Please complete delivery address"); return false; }
                if (!values.password || !/^(?=.*[a-z])(?=.*[A-Z]).{8,}$/.test(values.password)) {
                    setError("Password must be at least 8 characters with one uppercase and one lowercase letter");
                    return false;
                }
                break;
            default:
                break;
        }
        return true;
    };

    // --- Actions ---

    const handleEmailSubmit = async () => {
        setLoading(true);
        setError("");
        try {
            if (!email.includes("@")) throw new Error("Invalid email address");
            const res = await http.post("/auth/send-otp", { email });
            if (res.data.ok) {
                // Stay on step 7, but show OTP input (handled by local state pattern or we can just render conditionally in step 7)
                setOtpSent(true);
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    };
    const [otpSent, setOtpSent] = useState(false);

    const handleOtpSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            const res = await http.post("/auth/verify-otp", { email, otp });
            if (res.data.ok && res.data.tempToken) {
                setTempToken(res.data.tempToken);
                http.defaults.headers.common['Authorization'] = `Bearer ${res.data.tempToken}`;
                setDirection(1);
                setStep(8); // Proceed to Delivery Details
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    };

    const calculateCartTotal = () => {
        let planPrice = 0;
        if (values.planCategory === "Trucker") {
            planPrice = lookupTruckersPrice(values.planType, values.planTerm);
        } else {
            planPrice = lookupPlanPrice(values.planType, values.planTerm, values.nvPref || "-", values.billingTerm);
        }
        const addOnsPrice = sumAddOnsPrice(values.addOns, values.billingTerm);
        setCalculatedAmount(planPrice + addOnsPrice);
    };

    const handleCheckout = async () => {
        setLoading(true);
        setError("");

        try {
            // BOTH Standard and Trucker go through Stripe Checkout
            const isTrucker = values.planCategory === "Trucker";
            const payload = {
                planType: values.planType,
                planTerm: values.planTerm,
                billingTerm: isTrucker ? "Weekly" : values.billingTerm,
                addOns: values.addOns,
                planCategory: values.planCategory,
                personalDetails: {
                    firstName: values.firstName,
                    lastName: values.lastName,
                    phone: values.contact,
                    iam: values.iam,
                    startDate: values.startDate,
                    password: values.password,
                    address: {
                        street: values.street,
                        city: values.city,
                        postal: values.postal,
                        itsA: values.itsA,
                        buzz: values.buzz
                    },
                    preferences: {
                        nvPref: isTrucker ? "-" : (values.nvPref || "-"),
                        nvDays: (values.nvDays || []).map(d => d.toLowerCase()).join(','),
                        allergies: values.allergies
                    }
                }
            };

            const res = await http.post("/payment/create-checkout-session", payload, {
                headers: { "Authorization": `Bearer ${tempToken}` }
            });

            if (res.data.ok && res.data.url) {
                window.location.href = res.data.url;
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    };

    // --- Render Helpers ---
    const progressPercentage = ((step - 1) / (TOTAL_STEPS - 1)) * 100;

    // --- Steps ---

    const renderStep1 = () => (
        <div className="space-y-6">
            <h2 className="text-3xl font-bold text-white text-center mb-8">Tell us about yourself</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <OptionCard
                    selected={values.iam === "New Gym Bro"}
                    onClick={() => handleChange('iam', 'New Gym Bro')}
                    icon={User}
                    title="New Gym Bro"
                    description="Just getting started to bulk up or get lean"
                />
                <OptionCard
                    selected={values.iam === "New Gym Babe"}
                    onClick={() => handleChange('iam', 'New Gym Babe')}
                    icon={User}
                    title="New Gym Babe"
                    description="Starting fresh on your fitness journey"
                />
                <OptionCard
                    selected={values.iam === "Returning Gym Bro"}
                    onClick={() => handleChange('iam', 'Returning Gym Bro')}
                    icon={User}
                    title="Returning Gym Bro"
                    description="Welcome back to the grind"
                />
                <OptionCard
                    selected={values.iam === "Returning Gym Babe"}
                    onClick={() => handleChange('iam', 'Returning Gym Babe')}
                    icon={User}
                    title="Returning Gym Babe"
                    description="Ready to crush those goals again"
                />
            </div>
        </div>
    );

    const renderStep2 = () => (
        <div className="space-y-6">
            <h2 className="text-3xl font-bold text-white text-center mb-8">What are you looking for?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <OptionCard
                    selected={values.planCategory === "Standard"}
                    onClick={() => {
                        handleChange('planCategory', 'Standard');
                        handleChange('planType', '');
                        handleChange('planTerm', '');
                        handleChange('nvPref', '');
                        handleChange('nvDays', []);
                    }}
                    icon={Utensils}
                    title="Standard Meal Plans"
                    description="Fresh, high-protein meals delivered Monday to Friday"
                    badge="Most Popular"
                />
                <OptionCard
                    selected={values.planCategory === "Trucker"}
                    onClick={() => {
                        handleChange('planCategory', 'Trucker');
                        handleChange('planType', '');
                        handleChange('planTerm', '');
                        handleChange('billingTerm', 'Weekly');
                        handleChange('nvPref', '-');
                        handleChange('nvDays', []);
                    }}
                    icon={Truck}
                    title="Trucker Plans"
                    description="Bulk weekly delivery built specifically for the road"
                />
            </div>
        </div>
    );

    const renderStep3 = () => {
        const isStandard = values.planCategory === "Standard";
        const types = isStandard
            ? ["2 Meals a Day", "3 Meals a Day"]
            : ["Truckers 10 Meals a Week", "Truckers 12 Meals a Week"];

        const termsMap = isStandard ? standardPlanTerms : truckerPlanTerms;

        return (
            <div className="space-y-6">
                <h2 className="text-3xl font-bold text-white text-center mb-8">Customize your Plan</h2>

                <div className="space-y-4">
                    <Label className="text-gray-300 font-semibold text-lg">Meal Plan Type</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {types.map(t => (
                            <OptionCard
                                key={t}
                                selected={values.planType === t}
                                onClick={() => {
                                    handleChange('planType', t);
                                    handleChange('planTerm', '');
                                    handleChange('nvPref', '');
                                    handleChange('nvDays', []);
                                }}
                                title={t}
                            />
                        ))}
                    </div>
                </div>

                {values.planType && (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4 mt-8">
                        <Label className="text-gray-300 font-semibold text-lg">Meal Plan Term</Label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {termsMap[values.planType]?.map(term => (
                                <OptionCard
                                    key={term}
                                    selected={values.planTerm === term}
                                    onClick={() => {
                                        handleChange('planTerm', term);
                                        handleChange('nvPref', '');
                                        handleChange('nvDays', []);
                                    }}
                                    title={term}
                                />
                            ))}
                        </div>
                    </motion.div>
                )}

                {isStandard && (
                    <div className="mt-6">
                        <Label className="text-gray-300 font-semibold mb-2 block">Billing Cycle</Label>
                        <Select value={values.billingTerm} onValueChange={(v) => handleChange('billingTerm', v)}>
                            <SelectTrigger className="bg-gray-900 border-gray-700 text-white h-14 text-lg">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="Weekly">📅 Weekly Billing</SelectItem>
                                <SelectItem value="Monthly">📅 Monthly Billing</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                )}
            </div>
        );
    };

    const renderStep4 = () => {
        const isStandard = values.planCategory === "Standard";
        const needsNVPref = isStandard && values.planTerm && values.planTerm.includes("Non-Veg") && values.planTerm !== "5 Days Veg";
        const currentNVPrefOptions = values.planType && values.planTerm ? nvPrefOptions[values.planType]?.[values.planTerm] || [] : [];

        return (
            <div className="space-y-6">
                <h2 className="text-3xl font-bold text-white text-center mb-8">Preferences & Allergies</h2>

                {needsNVPref && currentNVPrefOptions.length > 0 && (
                    <div className="space-y-4 mb-8">
                        <Label className="text-gray-300 font-semibold text-lg">Non-Veg Preference</Label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {currentNVPrefOptions.map(pref => (
                                <OptionCard
                                    key={pref}
                                    selected={values.nvPref === pref}
                                    onClick={() => handleChange('nvPref', pref)}
                                    title={pref}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {isStandard && values.planTerm === "2 Days Non-Veg, 3 Days Veg" && values.nvPref && (
                    <div className="space-y-4 mb-8">
                        <Label className="text-gray-300 font-semibold text-lg">Pick Your 2 Non-Veg Days</Label>
                        <p className="text-gray-500 text-sm -mt-2">Select exactly 2 weekdays for non-veg meals</p>
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                            {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"].map(day => {
                                const isSelected = values.nvDays.includes(day);
                                const isDisabled = !isSelected && values.nvDays.length >= 2;
                                return (
                                    <button
                                        key={day}
                                        type="button"
                                        disabled={isDisabled}
                                        onClick={() => {
                                            const updated = isSelected
                                                ? values.nvDays.filter(d => d !== day)
                                                : [...values.nvDays, day];
                                            handleChange('nvDays', updated);
                                        }}
                                        className={`p-3 rounded-xl border-2 text-center font-medium transition-all duration-200 ${
                                            isSelected
                                                ? "bg-orange-500/20 border-orange-500 text-orange-400"
                                                : isDisabled
                                                    ? "bg-gray-900/30 border-gray-800 text-gray-600 cursor-not-allowed"
                                                    : "bg-gray-900 border-gray-700 text-gray-300 hover:border-orange-500"
                                        }`}
                                    >
                                        {day}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {!needsNVPref && isStandard && (
                    <div className="bg-gray-900/50 p-6 rounded-xl border border-gray-800 text-center mb-8">
                        <CheckCircle className="mx-auto text-green-500 mb-2" size={32} />
                        <p className="text-gray-300">No meat preference required for this plan.</p>
                    </div>
                )}

                {!isStandard && (
                    <div className="bg-gray-900/50 p-6 rounded-xl border border-gray-800 text-center mb-8">
                        <CheckCircle className="mx-auto text-green-500 mb-2" size={32} />
                        <p className="text-gray-300">Trucker plans have meat preferences built into the term selection.</p>
                    </div>
                )}

                <div>
                    <Label className="text-gray-300 font-semibold text-lg">Any Food Instructions / Allergies? (Optional)</Label>
                    <textarea
                        className="w-full mt-2 bg-gray-900 border-2 border-gray-700 text-white placeholder-gray-500 rounded-xl p-4 min-h-[120px] hover:border-orange-500 focus:border-orange-500 focus:outline-none transition-colors"
                        value={values.allergies}
                        onChange={(e) => handleChange('allergies', e.target.value)}
                        placeholder="E.g., No peanuts, extra spice, lactose intolerant..."
                    />
                </div>
            </div>
        );
    };

    const renderStep5 = () => (
        <div className="space-y-6">
            <h2 className="text-3xl font-bold text-white text-center mb-8">Power Up Your Plan</h2>
            <p className="text-gray-400 text-center -mt-6 mb-6">Select any extra add-ons (Optional)</p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {["5 Eggs Daily", "1 Veg Meal Daily", "1 Non Veg Meal Daily", "Bulk Me Up", "Protein Balls (2)", "Fruit on the Side"].map(addon => (
                    <label
                        key={addon}
                        className={`flex items-start gap-4 cursor-pointer p-5 rounded-2xl border-2 transition-all duration-300 ${values.addOns.includes(addon)
                            ? "bg-orange-500/10 border-orange-500 shadow-lg shadow-orange-500/20"
                            : "bg-gray-900 border-gray-700 hover:border-gray-500"
                            }`}
                    >
                        <div className="pt-1">
                            <motion.div
                                className={`w-6 h-6 rounded border-2 flex items-center justify-center ${values.addOns.includes(addon) ? 'bg-orange-500 border-orange-500' : 'border-gray-500'}`}
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                            >
                                {values.addOns.includes(addon) && <CheckCircle size={16} className="text-white" />}
                            </motion.div>
                            {/* Hidden checkbox for accessibility */}
                            <input
                                type="checkbox"
                                checked={values.addOns.includes(addon)}
                                onChange={() => handleAddOnToggle(addon)}
                                className="hidden"
                            />
                        </div>
                        <div>
                            <span className={`font-bold block ${values.addOns.includes(addon) ? "text-white" : "text-gray-300"}`}>
                                {addon}
                            </span>
                        </div>
                    </label>
                ))}
            </div>
        </div>
    );

    const renderStep6 = () => (
        <div className="max-w-2xl mx-auto space-y-8">
            <h2 className="text-3xl font-bold text-white text-center mb-8">Estimated Cost</h2>
            <Card className="bg-gray-900 border-gray-800 shadow-2xl">
                <CardContent className="pt-6">
                    <PricingCalculator
                        showHST={false}
                        planType={values.planType}
                        planTerm={values.planTerm}
                        nvPref={values.planCategory === "Trucker" ? "-" : values.nvPref}
                        billingTerm={values.billingTerm}
                        addOns={values.addOns}
                        isTrucker={values.planCategory === "Trucker"}
                    />
                </CardContent>
            </Card>
        </div>
    );

    const renderStep7 = () => (
        <div className="max-w-md mx-auto w-full space-y-6">
            <h2 className="text-3xl font-bold text-white text-center mb-8">Secure Your Account</h2>

            <Card className="bg-gray-900 border-gray-800 shadow-2xl">
                <CardContent className="pt-6">
                    {!otpSent ? (
                        <>
                            <div className="text-center mb-6">
                                <div className="bg-orange-500/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Mail className="text-orange-500" size={32} />
                                </div>
                                <h3 className="text-xl font-bold text-white">Email Verification</h3>
                                <p className="text-gray-400 mt-2">We'll send you a secure code to identify your account.</p>
                            </div>
                            <div className="space-y-4">
                                <Label className="text-gray-300">Email Address</Label>
                                <Input
                                    type="email"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    className="bg-gray-800 border-gray-700 text-white h-12 text-lg"
                                    placeholder="you@example.com"
                                    required
                                />
                                <Button onClick={handleEmailSubmit} disabled={loading || !email} className="w-full bg-orange-500 hover:bg-orange-600 text-white h-12 text-lg">
                                    {loading ? "Sending..." : "Send Verification Code"}
                                </Button>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="text-center mb-6">
                                <div className="bg-green-500/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <ShieldCheck className="text-green-500" size={32} />
                                </div>
                                <h3 className="text-xl font-bold text-white">Verify Email</h3>
                                <p className="text-gray-400 mt-2">Enter the 6-digit code sent to <br /><strong className="text-white">{email}</strong></p>
                            </div>
                            <form onSubmit={handleOtpSubmit} className="space-y-4">
                                <div>
                                    <Input
                                        value={otp}
                                        onChange={e => setOtp(e.target.value)}
                                        className="bg-gray-800 border-gray-700 text-white text-center text-3xl tracking-[0.5em] h-16 font-mono"
                                        placeholder="000000"
                                        maxLength={6}
                                        required
                                        autoFocus
                                    />
                                </div>
                                <Button type="submit" disabled={loading || otp.length < 6} className="w-full bg-orange-500 hover:bg-orange-600 text-white h-12 text-lg">
                                    {loading ? "Verifying..." : "Verify Code"}
                                </Button>
                                <button type="button" onClick={() => { setOtpSent(false); setOtp(""); }} className="text-sm text-gray-400 hover:text-orange-500 w-full text-center mt-4">
                                    Wrong email? Go back
                                </button>
                            </form>
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );

    const renderStep8 = () => (
        <div className="space-y-6">
            <h2 className="text-3xl font-bold text-white text-center mb-8">Delivery Details</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Personal Info */}
                <div className="space-y-4 bg-gray-900/50 p-6 rounded-2xl border border-gray-800">
                    <h3 className="text-xl font-bold text-white flex items-center gap-2 mb-4"><User className="text-orange-500" /> Personal Info</h3>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label className="text-gray-400">First Name</Label>
                            <Input value={values.firstName} onChange={e => handleChange('firstName', e.target.value)} className="bg-gray-900 border-gray-700 text-white mt-1 h-12" />
                        </div>
                        <div>
                            <Label className="text-gray-400">Last Name</Label>
                            <Input value={values.lastName} onChange={e => handleChange('lastName', e.target.value)} className="bg-gray-900 border-gray-700 text-white mt-1 h-12" />
                        </div>
                    </div>
                    <div>
                        <Label className="text-gray-400">Contact Number</Label>
                        <Input value={values.contact} onChange={e => handleChange('contact', e.target.value)} placeholder="416-555-0100" className="bg-gray-900 border-gray-700 text-white mt-1 h-12" />
                    </div>
                    <div>
                        <Label className="text-gray-400">Start Date</Label>
                        <Input
                            type="date"
                            value={values.startDate}
                            min={minStartDateToronto()}
                            onChange={(e) => {
                                const selectedDate = e.target.value;
                                if (selectedDate) {
                                    const day = new Date(selectedDate + 'T12:00:00Z').getUTCDay();
                                    if (day === 0 || day === 6) {
                                        alert('Please select a weekday (Monday-Friday) for your Start Date. Delivery is not available on Saturday and Sunday.');
                                        handleChange('startDate', nextServiceDayToronto());
                                        return;
                                    }
                                    if (selectedDate < tomorrowToronto()) {
                                        alert('Start date must be tomorrow or later.');
                                        return;
                                    }
                                }
                                handleChange('startDate', selectedDate);
                            }}
                            className={`bg-gray-900 border-gray-700 text-white mt-1 h-12 ${values.startDate && (new Date(values.startDate + 'T12:00:00Z').getUTCDay() === 0 || new Date(values.startDate + 'T12:00:00Z').getUTCDay() === 6) ? 'border-red-500' : ''}`}
                        />
                    </div>
                    <div>
                        <Label className="text-gray-400">Create Password</Label>
                        <Input
                            type="password"
                            value={values.password}
                            onChange={e => handleChange('password', e.target.value)}
                            placeholder="8+ chars, upper & lowercase"
                            className="bg-gray-900 border-gray-700 text-white mt-1 h-12"
                        />
                    </div>
                </div>

                {/* Address */}
                <div className="space-y-4 bg-gray-900/50 p-6 rounded-2xl border border-gray-800">
                    <h3 className="text-xl font-bold text-white flex items-center gap-2 mb-4"><MapPin className="text-orange-500" /> Location</h3>
                    <div>
                        <Label className="text-gray-400">Street Number & Name</Label>
                        <Input value={values.street} onChange={e => handleChange('street', e.target.value)} className="bg-gray-900 border-gray-700 text-white mt-1 h-12" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label className="text-gray-400">City</Label>
                            <Input value={values.city} onChange={e => handleChange('city', e.target.value)} className="bg-gray-900 border-gray-700 text-white mt-1 h-12" />
                        </div>
                        <div>
                            <Label className="text-gray-400">Postal Code</Label>
                            <Input value={values.postal} onChange={e => handleChange('postal', e.target.value)} className="bg-gray-900 border-gray-700 text-white mt-1 h-12" />
                        </div>
                    </div>
                    <div>
                        <Label className="text-gray-400">Building Type</Label>
                        <Select value={values.itsA} onValueChange={(v) => handleChange('itsA', v)}>
                            <SelectTrigger className="bg-gray-900 border-gray-700 text-white mt-1 h-12">
                                <SelectValue placeholder="Select..." />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="House">House</SelectItem>
                                <SelectItem value="House – Basement">House – Basement</SelectItem>
                                <SelectItem value="House – Front Door">House – Front Door</SelectItem>
                                <SelectItem value="Apartment">Apartment</SelectItem>
                                <SelectItem value="Condo">Condo</SelectItem>
                                <SelectItem value="Townhouse">Townhouse</SelectItem>
                                <SelectItem value="Business">Business</SelectItem>
                                <SelectItem value="Office">Office</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-gray-400">Buzz Code / Instructions</Label>
                        <Input value={values.buzz} onChange={e => handleChange('buzz', e.target.value)} placeholder="Leave at door, buzzer 123..." className="bg-gray-900 border-gray-700 text-white mt-1 h-12" />
                    </div>
                </div>
            </div>
        </div>
    );

    const renderStep9 = () => (
        <div className="max-w-2xl mx-auto space-y-8">
            <div className="text-center">
                <h2 className="text-3xl font-bold text-white mb-2">Review & Order</h2>
                <p className="text-gray-400">Almost there! Review your selections below.</p>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-3xl overflow-hidden shadow-2xl">
                <div className="bg-gray-800/50 p-6 flex justify-between items-center border-b border-gray-800">
                    <div>
                        <h3 className="text-xl font-bold text-white">{values.planCategory} Plan</h3>
                        <p className="text-orange-500 font-medium">{values.planType}</p>
                    </div>
                    <div className="text-right">
                        <span className="text-sm text-gray-400 block">{values.billingTerm} Billing</span>
                    </div>
                </div>

                <div className="p-6 space-y-6">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                            <span className="text-gray-500 block mb-1">Term Component</span>
                            <span className="text-white font-medium">{values.planTerm}</span>
                        </div>
                        {values.planCategory === "Standard" && (
                            <div>
                                <span className="text-gray-500 block mb-1">Meat Preference</span>
                                <span className="text-white font-medium">{values.nvPref || "N/A"}</span>
                            </div>
                        )}
                        <div>
                            <span className="text-gray-500 block mb-1">Start Date</span>
                            <span className="text-white font-medium">{values.startDate}</span>
                        </div>
                        <div>
                            <span className="text-gray-500 block mb-1">Email</span>
                            <span className="text-white font-medium">{email}</span>
                        </div>
                    </div>

                    {values.addOns.length > 0 && (
                        <div className="border-t border-gray-800 pt-6">
                            <span className="text-gray-500 block mb-2 text-sm">Selected Add-ons</span>
                            <div className="flex flex-wrap gap-2">
                                {values.addOns.map(a => (
                                    <span key={a} className="bg-orange-500/20 text-orange-400 text-xs px-3 py-1 rounded-full border border-orange-500/30">
                                        {a}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="border-t border-gray-800 pt-6">
                        <PricingCalculator
                            planType={values.planType}
                            planTerm={values.planTerm}
                            nvPref={values.planCategory === "Trucker" ? "-" : values.nvPref}
                            billingTerm={values.billingTerm}
                            addOns={values.addOns}
                            isTrucker={values.planCategory === "Trucker"}
                        />
                    </div>
                </div>
            </div>

            {error && <div className="max-w-xl mx-auto mb-6"><Alert variant="destructive"><AlertTitle>Error</AlertTitle><AlertDescription>{error}</AlertDescription></Alert></div>}

            <Button
                onClick={handleCheckout}
                disabled={loading}
                className="w-full bg-gradient-to-r from-orange-600 to-orange-400 hover:from-orange-500 hover:to-orange-300 transform transition-all hover:scale-[1.02] text-white h-16 text-xl rounded-2xl shadow-xl shadow-orange-500/20 font-bold"
            >
                {loading ? "Processing..." : "Proceed to Payment"}
            </Button>
        </div>
    );

    const steps = [
        renderStep1, renderStep2, renderStep3, renderStep4,
        renderStep5, renderStep6, renderStep7, renderStep8, renderStep9
    ];

    return (
        <div className="min-h-screen bg-black flex flex-col items-center justify-start pt-12 pb-24 px-4 sm:px-6">

            {/* Minimal Header */}
            <div className="w-full max-w-4xl mx-auto flex items-center justify-between mb-12">
                <button
                    onClick={() => navigate('/')}
                    className="text-gray-400 hover:text-white transition-colors flex items-center gap-2 font-medium"
                >
                    <ChevronLeft size={20} /> Exit
                </button>
                <div className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-yellow-500">
                    Just Protein
                </div>
            </div>

            {/* Progress Bar container */}
            <div className="w-full max-w-4xl mx-auto mb-12 relative">
                <div className="h-2 bg-gray-900 rounded-full overflow-hidden w-full absolute top-1/2 -translate-y-1/2">
                    <motion.div
                        className="h-full bg-gradient-to-r from-orange-600 to-orange-400 rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${progressPercentage}%` }}
                        transition={{ duration: 0.5, ease: "easeInOut" }}
                    />
                </div>
                {/* Optional dots for each step could go here, but a continuous bar is cleaner */}
            </div>

            {/* Error Toast / Alert (Top level) */}
            <AnimatePresence>
                {error && step !== 9 && ( /* Hide on step 9 as it has its own alert placement */
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="w-full max-w-xl mx-auto mb-8"
                    >
                        <Alert variant="destructive" className="bg-red-500/10 border-red-500/50 text-red-500">
                            <AlertDescription className="text-center font-medium">{error}</AlertDescription>
                        </Alert>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Step Content Wrapper with Animation */}
            <div className="w-full max-w-4xl mx-auto relative min-h-[500px]">
                <AnimatePresence mode="wait" custom={direction}>
                    <motion.div
                        key={step}
                        custom={direction}
                        variants={slideVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{
                            x: { type: "spring", stiffness: 300, damping: 30 },
                            opacity: { duration: 0.2 }
                        }}
                        className="w-full"
                    >
                        {steps[step - 1]()}
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* Navigation Buttons */}
            {step < TOTAL_STEPS && (
                <div className="fixed bottom-0 left-0 w-full bg-black/80 backdrop-blur-md border-t border-gray-800 p-4 z-50">
                    <div className="max-w-4xl mx-auto flex justify-between items-center">
                        <Button
                            variant="ghost"
                            onClick={prevStep}
                            disabled={step === 1 || loading}
                            className={`text-gray-400 hover:text-white hover:bg-gray-900 ${step === 1 ? 'opacity-0 pointer-events-none' : ''}`}
                        >
                            Back
                        </Button>

                        <Button
                            onClick={nextStep}
                            disabled={loading || (step === 7 && !tempToken)}
                            className="bg-white text-black hover:bg-gray-200 rounded-full px-8 py-6 text-lg font-bold shadow-xl shadow-white/10"
                        >
                            Next Step <ChevronRight size={20} className="ml-2" />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
